import { ensureDir, testAccess } from '@/utils/fs/fs-extra.utils';
import { ElectronApi } from './electron.api';
import { LibName, remoteLibsConfig } from '@/resources/remote-libs';
import { getOs } from '@/utils/platform';

export class RemoteLibsService {
  private static Downloading: Record<
    string,
    {
      loading: boolean;
      completed?: boolean;
    }
  > = {};
  private async getLibsDir() {
    const path = ElectronApi.path;
    const fs = ElectronApi.fs;
    if (!fs || !path) return;
    // 模版样例保存目录
    const libsDir = path.join(
      (await ElectronApi.getPath('userData')) ?? '',
      'Libs'
    );
    await ensureDir(libsDir);
    return libsDir;
  }

  /**
   * 下载lib，并返回filename
   * @param libName
   * @param url
   * @returns
   */
  private async downloadLib(filename: string, url: string): Promise<string> {
    if (!RemoteLibsService.Downloading[filename]) {
      RemoteLibsService.Downloading[filename] = {
        loading: false,
        completed: false
      };
    }
    const download = async () => {
      try {
        if (RemoteLibsService.Downloading[filename]) {
          RemoteLibsService.Downloading[filename].loading = true;
          RemoteLibsService.Downloading[filename].completed = false;
        }
        const path = ElectronApi.path;
        const fs = ElectronApi.fs;
        const libsDir = await this.getLibsDir();
        if (!fs || !path || !libsDir) return;
        const res = await window
          .fetch(`${url}?random=${Date.now()}`)
          .then((r) => r.arrayBuffer());

        await fs.promises.writeFile(filename, Buffer.from(res), {
          mode: '0777'
        });
        return filename;
      } catch (error) {
        console.log(error);
      } finally {
        if (RemoteLibsService.Downloading[filename]) {
          RemoteLibsService.Downloading[filename].loading = false;
          RemoteLibsService.Downloading[filename].completed = true;
        }
      }
    };
    if (!RemoteLibsService.Downloading[filename]?.loading) {
      await download();
    }
    let timer = 0;
    return new Promise((resovle, reject) => {
      timer = window.setInterval(() => {
        if (RemoteLibsService.Downloading[filename].completed) {
          window.clearInterval(timer);
          resovle(filename);
        }
        if (!RemoteLibsService.Downloading[filename]) {
          window.clearInterval(timer);
          reject();
        }
      }, 50);
    });
  }

  private getLibConfig(libName: LibName) {
    return remoteLibsConfig
      .find((item) => item.libName == libName)
      ?.libs.find((item) => item.paltform == getOs());
  }

  private async _filename(libName: LibName) {
    const path = ElectronApi.path;
    const fs = ElectronApi.fs;
    const libsDir = await this.getLibsDir();
    if (!fs || !path || !libsDir) return;
    const { url, version = 1 } = this.getLibConfig(libName) ?? {};
    if (!url) return; // 当前平台无配置时返回 undefined，触发 fallback
    const ext = url?.split('/')?.pop()?.split?.('.')?.[1] ?? '';
    const _filename = path.join(
      libsDir,
      [libName, 'version', version].join('_') + (ext ? '.' + ext : '')
    );
    return { filename: _filename, url };
  }

  async exists(libName: LibName) {
    const { filename: _filename } = (await this._filename(libName)) ?? {};
    if (!_filename) return false;
    return await testAccess(_filename);
  }

  async getLibFilename(libName: LibName, forceDownload?: boolean) {
    const { filename: _filename, url } = (await this._filename(libName)) ?? {};
    if (!_filename) {
      // Linux等未配置远程库的平台，回退到系统安装的命令
      if (getOs() === 'linux') {
        const sysPath = '/usr/bin/' + libName;
        console.log(`[RemoteLibsService] Linux fallback: checking ${sysPath}`);
        if (await testAccess(sysPath)) {
          console.log(`[RemoteLibsService] Using system ${sysPath}`);
          return sysPath;
        }
      }
      console.log(`[RemoteLibsService] ${libName} not available, os=${getOs()}`);
      return;
    }

    if (forceDownload || !(await testAccess(_filename))) {
      if (!url) return;
      // 重新下载
      return await this.downloadLib(_filename, url);
    } else {
      return _filename;
    }
  }
}
